import { MouseEvent, PropsWithChildren } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Typography } from '@mui/material';
import PolarisIcon from '../_common/PolarisIcon';

type ActionDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  onClose: () => void;
  showCloseIcon?: boolean;
};

export const ActionDialog = ({
  open,
  title,
  description,
  children,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  onClose,
  showCloseIcon = true
}: PropsWithChildren<ActionDialogProps>) => {
  if (!open) return null;

  const handleBackdropClick = () => onClose();

  const stopPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Backdrop onClick={handleBackdropClick}>
      <DialogContainer onClick={stopPropagation}>
        {showCloseIcon && (
          <CloseIconButton onClick={onClose} aria-label="Close dialog">
            <PolarisIcon icon="close" />
          </CloseIconButton>
        )}
        <DefaultContent>
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>
          {description && <DescriptionText variant="body2">{description}</DescriptionText>}
          {!!children && children}
          <ActionsCentered>
            <PrimaryButton color="secondary" variant="contained" onClick={onPrimaryAction}>
              {primaryActionLabel}
            </PrimaryButton>
            {secondaryActionLabel && onSecondaryAction && <SecondaryButton onClick={onSecondaryAction}>{secondaryActionLabel}</SecondaryButton>}
          </ActionsCentered>
        </DefaultContent>
      </DialogContainer>
    </Backdrop>
  );
};

const Backdrop = styled('div')`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
`;

const DialogContainer = styled('div')`
  background: #2f3142;
  color: #ffffff;
  min-width: 520px;
  max-width: 720px;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  position: relative;
`;

const CloseIconButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #ffffff;
  }
`;

const DefaultContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DescriptionText = styled(Typography)`
  color: #a3a8ba;
  font-size: 16px;
`;

const ActionsCentered = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  gap: 8px;
`;

const PrimaryButton = styled(Button)`
  display: flex;
  padding: 8px 12px;
  align-items: center;
`;

const SecondaryButton = styled(Button)`
  color: #a3a8ba;
  text-transform: none;
  min-width: unset;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    background-color: transparent;
    color: #ffffff;
  }
`;
